import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useChatMessages,
  useConversations,
  useMarkRead,
  type ChatMessage,
  type ConvSummary,
} from '@/hooks/useMessenger';
import { Composer } from './Composer';

interface ChatThreadProps {
  threadId: string | null;
  onBack: () => void;
}

export function ChatThread({ threadId, onBack }: ChatThreadProps) {
  const { t } = useTranslation();
  const { data: convs = [] } = useConversations();
  const { data: messages = [] } = useChatMessages(threadId);
  const markRead = useMarkRead();
  const conv = convs.find((c) => c.id === threadId);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (threadId && conv && conv.unread > 0) markRead.mutate(threadId);
  }, [threadId, conv?.unread]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  if (!threadId || !conv) {
    return (
      <main className="msg-chat-pane">
        <div className="chat-empty">
          <span className="material-symbols-outlined">forum</span>
          {t('messenger.empty_pane')}
        </div>
      </main>
    );
  }

  return (
    <main className="msg-chat-pane">
      <ChatTopBar conv={conv} onBack={onBack} />
      <div className="chat-messages" ref={scrollRef}>
        {messages.map((m, i) => (
          <MsgRow
            key={m.id}
            msg={m}
            avatarGradient={conv.avatarGradient}
            consecutive={i > 0 && messages[i - 1]?.fromSelf === m.fromSelf}
          />
        ))}
      </div>
      <Composer threadId={threadId} />
    </main>
  );
}

function ChatTopBar({ conv, onBack }: { conv: ConvSummary; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="chat-topbar">
      <button type="button" className="chat-back-btn" onClick={onBack} aria-label="Back">
        <span className="material-symbols-outlined">arrow_back</span>
      </button>
      <div className="chat-topbar-avatar" style={{ background: conv.avatarGradient }}>
        {conv.initials}
      </div>
      <div className="chat-topbar-info">
        <div className="chat-topbar-name">{conv.name}</div>
        <div className="chat-topbar-status">
          {t(`messenger.status_${conv.online}`)}
        </div>
      </div>
    </header>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function BubbleContent({ msg }: { msg: ChatMessage }) {
  if (msg.type === 'IMAGE') {
    return (
      <a href={msg.text} target="_blank" rel="noopener noreferrer" className="bubble-image-link">
        <img src={msg.text} alt={msg.fileName ?? 'image'} className="bubble-image" loading="lazy" />
      </a>
    );
  }
  if (msg.type === 'FILE') {
    return (
      <a href={msg.text} target="_blank" rel="noopener noreferrer" className="bubble-file">
        <span className="material-symbols-outlined bubble-file-icon">description</span>
        <span className="bubble-file-meta">
          <span className="bubble-file-name">{msg.fileName ?? 'file'}</span>
          {typeof msg.fileSize === 'number' && (
            <span className="bubble-file-size">{formatBytes(msg.fileSize)}</span>
          )}
        </span>
      </a>
    );
  }
  if (msg.type === 'LOCATION') {
    const [lat, lng] = msg.text.split(',');
    const mapsUrl = `https://maps.google.com/?q=${lat},${lng}`;
    return (
      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="bubble-location">
        <span className="material-symbols-outlined bubble-location-icon">location_on</span>
        <span className="bubble-location-meta">
          <span className="bubble-location-title">{lat && lng ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}` : msg.text}</span>
          <span className="bubble-location-sub">Open in Maps</span>
        </span>
      </a>
    );
  }
  return <>{msg.text}</>;
}

function MsgRow({
  msg,
  avatarGradient,
  consecutive,
}: {
  msg: ChatMessage;
  avatarGradient: string;
  consecutive: boolean;
}) {
  const rich = msg.type === 'IMAGE' || msg.type === 'FILE' || msg.type === 'LOCATION';
  const bubbleCls =
    (msg.fromSelf ? 'bubble bubble-sent' : 'bubble bubble-recv') + (rich ? ' bubble-rich' : '');
  return (
    <div className={`msg-row ${msg.fromSelf ? 'sent' : 'recv'}${consecutive ? ' no-avatar' : ''}`}>
      {!msg.fromSelf && (
        <div className="msg-avatar" style={{ background: avatarGradient }}>
          {msg.authorInitials}
        </div>
      )}
      <div className={bubbleCls}>
        <BubbleContent msg={msg} />
        <span className="bubble-time">{msg.time}</span>
      </div>
      {msg.fromSelf && (
        <div className="msg-status">
          <span className="material-symbols-outlined">done_all</span>
        </div>
      )}
    </div>
  );
}
